import React, { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";

import { FormInput } from "../../../SharedComponents/FormInput/FormInput";
import { Button, ButtonLoading } from "../../../SharedComponents/Button/Button";
import { LoginOnboardingInterstitial } from "../LoginOnboardingInterstitial/LoginOnboardingInterstitial";
import { InlineInfoBox } from "../../../SharedComponents/InlineInfoBox/InlineInfoBox";
import { authenticateEflowUser } from "./authenticateEflowUser";
import { completeUserCreationMutation } from "./completeUserCreationMutation";
import { handleGraphQLErrors } from "../../../../utils/handleGraphQLError";
import { useLoginAndRedirect } from "../../../../Hooks/useLoginAndRedirect";
import { passwordValidator } from "../../../../utils/passwordValidator";

/**
 * @typedef {'IDLE' | 'LOADING' | 'GENERIC_ERROR'} ComponentState
 */

export const LoginOnboardingOnboardingForm = ({
  defaultEmail,
  defaultRedirect,
}) => {
  /** @type {[ComponentState, (state: ComponentState) => void]} */
  const [componentState, setComponentState] = useState("IDLE");

  // Setup needed hooks
  const { t } = useTranslation();
  const [completeSetup] = useMutation(completeUserCreationMutation);
  const loginAndRedirect = useLoginAndRedirect();

  // Reference to the password input for focusing
  const passwordInputRef = useRef();

  // Focus the password field on mount
  useEffect(
    () => passwordInputRef.current && passwordInputRef.current.focus(),
    []
  );

  // Submit handler
  async function onSubmit(data) {
    setComponentState("LOADING");

    // Change SSO password
    try {
      await completeSetup({
        variables: {
          email: data.email,
          password: data.password,
        },
      });
    } catch (err) {
      handleGraphQLErrors(err);
      return setComponentState("GENERIC_ERROR");
    }

    // Login the user and redirect to the appropriate url
    try {
      await loginAndRedirect({
        email: data.email,
        password: data.password,
        redirectLocation: defaultRedirect,
      });
    } catch (err) {
      handleGraphQLErrors(err);
      return setComponentState("GENERIC_ERROR");
    }
  }

  // Formik form configuration
  /** @type {import('formik').FormikConfig} */
  const formConfig = {
    initialValues: {
      email: defaultEmail,
      oldPassword: "",
      password: "",
      confirmedPassword: "",
    },
    onSubmit: onSubmit,
    validate: async (values) => {
      // Check eflow password in validation
      setComponentState("LOADING");

      const errors = {};

      let eflowResponse;
      try {
        eflowResponse = await authenticateEflowUser({
          email: values.email,
          password: values.oldPassword,
        });
      } catch (err) {
        console.error(err);

        // Something happened during the request
        return (errors.oldPassword = "errors.generic");
      }

      // Eflow user not found
      if (
        eflowResponse.status === "error" &&
        eflowResponse.message === "User not found"
      ) {
        errors.oldPassword = "errors.onboardingForm.noUserWithGivenEmail";
      }

      // Eflow responded with an error message
      if (!errors.oldPassword && eflowResponse.status === "error") {
        console.error(eflowResponse.message);

        errors.oldPassword = "errors.generic";
      }

      if (
        eflowResponse.status === "success" &&
        !eflowResponse.data.authenticated
      ) {
        errors.oldPassword = "onboardingForm.currentPasswordIsIncorrect";
      }

      setComponentState("IDLE");

      return errors;
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("validation.emailInvalid")
        .required("validation.emailRequired"),
      oldPassword: Yup.string().required("validation.passwordRequired"),
      password: passwordValidator.required("validation.passwordRequired"),
      confirmedPassword: Yup.string()
        .required("validation.passwordRequired")
        .oneOf(
          [Yup.ref("password"), null],
          "validation.passwordMatchesOtherField"
        ),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  };

  const isLoading = ["LOADING"].includes(componentState);

  return (
    <Formik {...formConfig}>
      {() => (
        <Form>
          <FormInput
            name="email"
            type="text"
            label="generic.email"
            disabled={true}
          />
          <LoginOnboardingInterstitial showButton={false} />
          <FormInput
            name="oldPassword"
            type="password"
            label="onboardingForm.currentPassword"
            ref={passwordInputRef}
          />
          <FormInput
            name="password"
            type="password"
            label="onboardingForm.newPassword"
          />
          <FormInput
            name="confirmedPassword"
            type="password"
            label="onboardingForm.repeatNewPassword"
          />
          {componentState === "GENERIC_ERROR" && (
            <InlineInfoBox type="danger">{t("errors.generic")}</InlineInfoBox>
          )}
          <div className="LoginOnboardingForm__submit">
            <div className="LoginOnboardingForm__submit-button">
              <Button
                buttonStyle="Button__primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading && <ButtonLoading />}
                {t("onboardingForm.renewPasswordButton")}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
